<template>
  <base-legal-page
    kicker="Devprompt"
    title="Devprompt Privacy Policy"
    updated="September 2026"
  >
    <h2>1. The short version</h2>
    <p>
      Devprompt has no accounts and no sign-in, and it does not collect personal information about you. Three things leave your device: what is needed to process a purchase (handled by Apple and our payments provider), a once-a-day check for a newer version, and anonymous usage counts — which screens and questions are opened, never anything you type or say — tied to a random token, not to you.
    </p>

    <h2>2. Information we collect</h2>
    <p>
      <strong>No personal information.</strong> Devprompt does not ask for your name, your email address, or any other personal detail, and there is nothing to sign in to. We do not build a profile of you and we do not use advertising or tracking software.
    </p>
    <p>
      <strong>Anonymous usage events.</strong> The app sends our own server (api.hackerhang.com) short event records: an event name from a fixed list — a question was opened, a check was revealed, a reminder was set, a Companion session ended — with a few labels such as the question's id, its level, or a count, plus the app version and platform. Each install generates a random token on first launch so we can count how many installs are active; it is not your device identifier, not the advertising identifier, and it links to nothing about you. Events never contain anything you typed, searched for, or said.
    </p>
    <p>
      <strong>Update check.</strong> Once a day the app asks the same server whether a newer version exists, sending only the app version and platform.
    </p>

    <h2>3. What stays on your device</h2>
    <p>
      The app saves a small amount of information locally so that it works the way you would expect:
    </p>
    <ul>
      <li><strong>Your progress:</strong> which questions you have marked done or set aside for review</li>
      <li><strong>Your streak and history:</strong> the days you have studied</li>
      <li><strong>Your settings:</strong> preferences such as reduced motion</li>
      <li><strong>Recent searches:</strong> so the search screen can offer them again</li>
    </ul>
    <p>
      All of this is stored on your device only. It is never uploaded, never backed up to us, and never shared. We cannot see it. Deleting the app deletes all of it.
    </p>

    <h2>4. Purchases</h2>
    <p>
      Devprompt offers a one-time in-app purchase that unlocks the full library. Purchases are processed by Apple, under Apple's own privacy policy. We never see your payment details.
    </p>
    <p>
      To remember that you have bought the app across reinstalls and devices, we use RevenueCat, a purchase-management service. RevenueCat receives an anonymous identifier generated on your device, together with the purchase receipt Apple issues. It does not receive your name, your email address, or anything you have read or done in the app. Their privacy policy is at
      <a
        href="https://www.revenuecat.com/privacy"
        target="_blank"
        rel="noopener"
      >revenuecat.com/privacy</a>.
    </p>
    <p>
      Apart from purchases, the update check and the anonymous events above, the app makes no network requests.
    </p>

    <h2>5. Permissions</h2>
    <p>
      Devprompt does not access your photos, contacts, location, calendar, or health data. The Companion feature listens through the microphone only while you have started it, turns speech into text on the device, and never records or uploads audio or the transcript; what it keeps is which library questions came up. If you turn on the daily reminder, it is a local notification scheduled on the phone. The app does not use the advertising identifier and does not track you across other apps or websites.
    </p>

    <h2>6. Children's privacy</h2>
    <p>
      Devprompt is a study tool for software developers and is not directed at children under 13. The app collects no personal information from anyone, and so none from children.
    </p>

    <h2>7. Your rights</h2>
    <p>
      The usage events we hold are anonymous and cannot be matched to you, so there is nothing for us to show you, correct, or delete about you. You can remove everything the app has stored on your phone, including the random install token, by deleting the app. If you have bought the full library and later reinstall, "Restore purchases" in the app will recover it through Apple.
    </p>

    <h2>8. Changes to this policy</h2>
    <p>
      If the app ever begins collecting information, this page will be updated before that change ships, and the "Last updated" date above will change with it.
    </p>

    <h2>9. Contact us</h2>
    <p>If you have any questions about this policy, please get in touch:</p>
    <div class="hh-legal__contact">
      <div><strong>Email:</strong> <a href="mailto:info@hackerhang.com">info@hackerhang.com</a></div>
      <div><strong>Website:</strong> <a href="https://hackerhang.com">hackerhang.com</a></div>
    </div>
  </base-legal-page>
</template>

<script>
  export default {
    name: 'DevpromptPrivacy',

    metaInfo: { title: 'Devprompt Privacy Policy' },
  }
</script>
